import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, ReactiveFormsModule, FormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn, FormGroupDirective, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ErrorStateMatcher, MatCommonModule } from '@angular/material/core';
import AddressAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/common/embedded/address/address.entity';
import { AddressTypeAttributes, LocationOneAttributes, LocationThreeAttributes, LocationTwoAttributes } from '@albatrosdeveloper/ave-models-package';
import { IAddressDTO } from '../interfaces/address-dto.interface';
import { UpdateSalesOfficeAddress } from '../interfaces/update-sales-office.interface';
import { AddressTypeService } from 'app/modules/components/common/address-type/address-type.service.service';
import { LocationOneService } from 'app/modules/components/locations/location-one/location-one.service.service';
import { LocationTwoService } from 'app/modules/components/locations/location-two/location-two.service';
import { LocationThreeService } from 'app/modules/components/locations/location-three/location-three.service';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

// Validadores personalizados
function latitudeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.trim() === '') {
      return null; // No es requerido
    }
    const value = String(control.value).replace(',', '.').trim();
    const numValue = parseFloat(value);

    // Validar formato
    if (!/^-?\d+(\.\d+)?$/.test(value)) {
      return { invalidFormat: true };
    }

    // Validar rango
    if (isNaN(numValue) || numValue < -90 || numValue > 90) {
      return { invalidRange: true };
    }

    return null;
  };
}

function longitudeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.trim() === '') {
      return null; // No es requerido
    }
    const value = String(control.value).replace(',', '.').trim();
    const numValue = parseFloat(value);

    // Validar formato
    if (!/^-?\d+(\.\d+)?$/.test(value)) {
      return { invalidFormat: true };
    }

    // Validar rango
    if (isNaN(numValue) || numValue < -180 || numValue > 180) {
      return { invalidRange: true };
    }

    return null;
  };
}

// Validador para autocomplete - verifica que el valor ingresado sea un ID válido de la lista
function autocompleteValidator(validIds: () => string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value || control.value.trim() === '') {
      return null;
    }
    const ids = validIds();
    if (!ids.includes(control.value)) {
      return { invalidSelection: true };
    }
    return null;
  };
}

// ErrorStateMatcher personalizado - muestra errores inmediatamente si el control es inválido
export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  constructor(private isInitializing: () => boolean) {}

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    // No mostrar errores durante la inicialización
    if (this.isInitializing()) {
      return false;
    }
    // Muestra errores si el control es inválido
    return !!(control && control.invalid);
  }
}

@Component({
  selector: 'app-sales-office-address-form',
  standalone: true,
  templateUrl: './sales-office-address-form.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatCommonModule,
    FormSectionComponent,
  ],
  // providers: [
  //   { provide: ErrorStateMatcher, useClass: ImmediateErrorStateMatcher },
  // ],
})
export class SalesOfficeAddressFormComponent implements OnInit, OnDestroy {
  @Input() address: UpdateSalesOfficeAddress | IAddressDTO | Partial<AddressAttributes> | null;
  @Output() addressChange = new EventEmitter<IAddressDTO>();
  @Output() formValidityChange = new EventEmitter<boolean>();

  addressForm: FormGroup;

  locationOneCtrl = new FormControl('', [
    Validators.required,
    autocompleteValidator(() => this.locationOneList.map(l => l._id.toString()))
  ]);
  locationTwoCtrl = new FormControl('', [
    autocompleteValidator(() => this.locationTwoList.map(l => l._id.toString()))
  ]);
  locationThreeCtrl = new FormControl('', [
    autocompleteValidator(() => this.locationThreeList.map(l => l._id.toString()))
  ]);
  addressTypeCtrl = new FormControl('', [
    Validators.required,
    autocompleteValidator(() => this.addressTypeList.map(t => t._id.toString()))
  ]);

  // Listas originales
  addressTypeList: AddressTypeAttributes[] = [];
  locationOneList: LocationOneAttributes[] = [];
  locationTwoList: LocationTwoAttributes[] = [];
  locationThreeList: LocationThreeAttributes[] = [];

  // ReplaySubjects filtrados para autocomplete
  filteredLocationOne: ReplaySubject<LocationOneAttributes[]> = new ReplaySubject<LocationOneAttributes[]>(1);
  filteredLocationTwo: ReplaySubject<LocationTwoAttributes[]> = new ReplaySubject<LocationTwoAttributes[]>(1);
  filteredLocationThree: ReplaySubject<LocationThreeAttributes[]> = new ReplaySubject<LocationThreeAttributes[]>(1);
  filteredAddressTypes: ReplaySubject<AddressTypeAttributes[]> = new ReplaySubject<AddressTypeAttributes[]>(1);

  // Subject para manejar unsubscribe
  private _onDestroy = new Subject<void>();

  // Validaciones
  latitudError: boolean = false;
  longitudError: boolean = false;
  latitudeErrorMessage: string = '';
  longitudeErrorMessage: string = '';

  // Flag para trackear inicialización
  isInitializing: boolean = true;

  // ErrorStateMatcher para controlar cuándo mostrar errores
  errorStateMatcher: ErrorStateMatcher;

  constructor(
    private readonly addressTypeService: AddressTypeService,
    private readonly locationOneService: LocationOneService,
    private readonly locationTwoService: LocationTwoService,
    private readonly locationThreeService: LocationThreeService,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    // Inicializar ErrorStateMatcher con función que retorna el estado de inicialización
    this.errorStateMatcher = new ImmediateErrorStateMatcher(() => this.isInitializing);

    this.addressForm = this.fb.group({
      name: ['', Validators.required],
      address: ['', Validators.required],
      reference: [''],
      latitude: ['', latitudeValidator()],
      longitude: ['', longitudeValidator()],
      postalCode: ['', Validators.required],
      contactName: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.fetchAllAddressType();
    this.fetchAllLocationOne();
    this.initializeFilteredObservables();

    if (this.address) {
      // Manejar diferentes tipos de DTO
      const hasGeolocation = 'geolocationPoint' in this.address;
      const hasLatLng = 'latitude' in this.address && 'longitude' in this.address;

      this.addressForm.patchValue({
        name: this.address.name || '',
        address: this.address.address || '',
        reference: this.address.reference || '',
        latitude: hasLatLng ? (this.address as any).latitude : (hasGeolocation ? (this.address as any).geolocationPoint?.[0] : ''),
        longitude: hasLatLng ? (this.address as any).longitude : (hasGeolocation ? (this.address as any).geolocationPoint?.[1] : ''),
        postalCode: this.address.postalCode || '',
        contactName: this.address.contactName || ''
      });
    }

    // Solo emitir cambios después de la inicialización
    this.addressForm.valueChanges.subscribe(() => {
      if (!this.isInitializing) {
        this.emitAddressData();
        this.emitFormValidity();
      }
    });

    this.locationOneCtrl.valueChanges.subscribe(() => {
      if (!this.isInitializing) {
        this.emitFormValidity();
      }
    });

    this.addressTypeCtrl.valueChanges.subscribe(() => {
      if (!this.isInitializing) {
        this.emitFormValidity();
      }
    });

    // Marcar como inicializado después de un breve delay para asegurar que todos los datos se carguen
    setTimeout(() => {
      this.isInitializing = false;

      // Marcar todos los controles como touched para mostrar errores si existen
      this.addressForm.markAllAsTouched();
      this.locationOneCtrl.markAsTouched();
      this.locationTwoCtrl.markAsTouched();
      this.locationThreeCtrl.markAsTouched();
      this.addressTypeCtrl.markAsTouched();

      // Actualizar validación de todos los controles
      this.addressForm.updateValueAndValidity();
      this.locationOneCtrl.updateValueAndValidity();
      this.locationTwoCtrl.updateValueAndValidity();
      this.locationThreeCtrl.updateValueAndValidity();
      this.addressTypeCtrl.updateValueAndValidity();

      // Forzar detección de cambios para actualizar la vista
      this.cdr.detectChanges();

      this.emitFormValidity();
    }, 300);
  }

  private buildAddressFromForm(): IAddressDTO {
    const formValue = this.addressForm.value;
    const locationOne = this.getSelectedLocationOne();
    const locationTwo = this.getSelectedLocationTwo();
    const locationThree = this.getSelectedLocationThree();
    const addressType = this.getSelectedAddressType();

    return {
      name: formValue.name || '',
      address: formValue.address || '',
      reference: formValue.reference || undefined,
      postalCode: formValue.postalCode || undefined,
      contactName: formValue.contactName || undefined,
      typeId: addressType?._id?.toString() || '',
      locationOneId: locationOne?._id?.toString() || undefined,
      locationTwoId: locationTwo?._id?.toString() || undefined,
      locationThreeId: locationThree?._id?.toString() || undefined,
      locationFourId: undefined,
      latitude: formValue.latitude ? String(formValue.latitude).trim() : undefined,
      longitude: formValue.longitude ? String(formValue.longitude).trim() : undefined,
      isPrincipal: undefined,
      statusId: undefined,
      _id: null
    };
  }

  getSelectedLocationOne(): LocationOneAttributes | null {
    const id = this.locationOneCtrl.value;
    return id ? this.locationOneList.find(l => l._id.toString() === id) : null;
  }

  getSelectedLocationTwo(): LocationTwoAttributes | null {
    const id = this.locationTwoCtrl.value;
    return id ? this.locationTwoList.find(l => l._id.toString() === id) : null;
  }

  getSelectedLocationThree(): LocationThreeAttributes | null {
    const id = this.locationThreeCtrl.value;
    return id ? this.locationThreeList.find(l => l._id.toString() === id) : null;
  }

  private getSelectedAddressType(): AddressTypeAttributes | null {
    const id = this.addressTypeCtrl.value;
    return id ? this.addressTypeList.find(t => t._id.toString() === id) : null;
  }

  private initializeFilteredObservables(): void {
    this.locationOneCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((value) => {
        const filtered = this.filterLocationOne(value || '');
        this.filteredLocationOne.next(filtered);
      });

    this.locationTwoCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((value) => {
        const filtered = this.filterLocationTwo(value || '');
        this.filteredLocationTwo.next(filtered);
      });

    this.locationThreeCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((value) => {
        const filtered = this.filterLocationThree(value || '');
        this.filteredLocationThree.next(filtered);
      });

    this.addressTypeCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe((value) => {
        const filtered = this.filterAddressTypes(value || '');
        this.filteredAddressTypes.next(filtered);
      });
  }

  private filterLocationOne(value: string): LocationOneAttributes[] {
    if (!this.locationOneList) return [];
    if (!value || value.trim() === '') return this.locationOneList;
    const filterValue = value.toLowerCase();
    return this.locationOneList.filter((location) =>
      location.name.toLowerCase().includes(filterValue)
    );
  }

  private filterLocationTwo(value: string): LocationTwoAttributes[] {
    if (!this.locationTwoList) return [];
    if (!value || value.trim() === '') return this.locationTwoList;
    const filterValue = value.toLowerCase();
    return this.locationTwoList.filter((location) =>
      location.name.toLowerCase().includes(filterValue)
    );
  }

  private filterLocationThree(value: string): LocationThreeAttributes[] {
    if (!this.locationThreeList) return [];
    if (!value || value.trim() === '') return this.locationThreeList;
    const filterValue = value.toLowerCase();
    return this.locationThreeList.filter((location) =>
      location.name.toLowerCase().includes(filterValue)
    );
  }

  private filterAddressTypes(value: string): AddressTypeAttributes[] {
    if (!this.addressTypeList) return [];
    if (!value || value.trim() === '') return this.addressTypeList;
    const filterValue = value.toLowerCase();
    return this.addressTypeList.filter((type) =>
      type.name.toLowerCase().includes(filterValue)
    );
  }

  fetchLocationOneName = (id: string): string => {
    const found = this.locationOneList.find((l) => l._id.toString() === id);
    return found ? found.name : '';
  };

  fetchLocationTwoName = (id: string): string => {
    const found = this.locationTwoList.find((l) => l._id.toString() === id);
    return found ? found.name : '';
  };

  fetchLocationThreeName = (id: string): string => {
    const found = this.locationThreeList.find((l) => l._id.toString() === id);
    return found ? found.name : '';
  };

  fetchAddressTypeName = (id: string): string => {
    const found = this.addressTypeList.find((t) => t._id.toString() === id);
    return found ? found.name : '';
  };

  private fetchAllAddressType(): void {
    this.addressTypeService.findToComboBox().subscribe({
      next: (res) => {
        this.addressTypeList = res.data;
        this.filteredAddressTypes.next(this.addressTypeList.slice());

        if (this.address) {
          // Manejar tanto typeId (DTO) como type (AddressAttributes)
          const typeId = 'typeId' in this.address ? (this.address as any).typeId : (this.address as any).type?._id?.toString();
          if (typeId) {
            this.addressTypeCtrl.setValue(typeId);
          }
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || 'Error al obtener tipos de dirección',
          'Cerrar',
          { duration: 5000 }
        );
      },
    });
  }

  private fetchAllLocationOne(): void {
    this.locationOneService.findToComboBox().subscribe({
      next: (res) => {
        this.locationOneList = res.data;
        this.filteredLocationOne.next(this.locationOneList.slice());

        if (this.address) {
          // Manejar tanto locationOneId (DTO) como locationOne (AddressAttributes)
          const locationOneId = 'locationOneId' in this.address ? (this.address as any).locationOneId : (this.address as any).locationOne?._id?.toString();
          if (locationOneId) {
            this.locationOneCtrl.setValue(locationOneId);
            const locationOne = this.locationOneList.find(l => l._id.toString() === locationOneId);
            if (locationOne) {
              this.fetchAllLocationTwo(locationOne);
            }
          }
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || 'Error al obtener países',
          'Cerrar',
          { duration: 5000 }
        );
      },
    });
  }

  private fetchAllLocationTwo(country: any): void {
    if (!country || !country.code) {
      return;
    }

    this.locationTwoService.findToComboBox(country.code).subscribe({
      next: (res) => {
        this.locationTwoList = res.data;
        this.filteredLocationTwo.next(this.locationTwoList.slice());

        if (this.address) {
          // Manejar tanto locationTwoId (DTO) como locationTwo (AddressAttributes)
          const locationTwoId = 'locationTwoId' in this.address ? (this.address as any).locationTwoId : (this.address as any).locationTwo?._id?.toString();
          if (locationTwoId) {
            this.locationTwoCtrl.setValue(locationTwoId);
            const locationTwo = this.locationTwoList.find(l => l._id.toString() === locationTwoId);
            if (locationTwo) {
              this.fetchAllLocationThree(locationTwo);
            }
          }
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || 'Error al obtener departamentos',
          'Cerrar',
          { duration: 5000 }
        );
      },
    });
  }

  private fetchAllLocationThree(department: any): void {
    if (!department || !department.code) {
      return;
    }

    this.locationThreeService.findToComboBox(department.code).subscribe({
      next: (res) => {
        this.locationThreeList = res.data;
        this.filteredLocationThree.next(this.locationThreeList.slice());

        if (this.address) {
          // Manejar tanto locationThreeId (DTO) como locationThree (AddressAttributes)
          const locationThreeId = 'locationThreeId' in this.address ? (this.address as any).locationThreeId : (this.address as any).locationThree?._id?.toString();
          if (locationThreeId) {
            this.locationThreeCtrl.setValue(locationThreeId);
          }
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || 'Error al obtener ciudades',
          'Cerrar',
          { duration: 5000 }
        );
      },
    });
  }

  onLocationOneSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedId = event.option.value;
    const selectedLocation = this.locationOneList.find(
      (l) => l._id.toString() === selectedId
    );

    if (selectedLocation) {
      this.locationTwoList = [];
      this.locationThreeList = [];
      this.locationTwoCtrl.setValue('');
      this.locationThreeCtrl.setValue('');
      this.fetchAllLocationTwo(selectedLocation);
    }

    this.emitAddressData();
  }

  onLocationTwoSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedId = event.option.value;
    const selectedLocation = this.locationTwoList.find(
      (l) => l._id.toString() === selectedId
    );

    if (selectedLocation) {
      this.locationThreeList = [];
      this.locationThreeCtrl.setValue('');
      this.fetchAllLocationThree(selectedLocation);
    }

    this.emitAddressData();
  }

  onLocationThreeSelected(event: MatAutocompleteSelectedEvent): void {
    this.emitAddressData();
  }

  onAddressTypeSelected(event: MatAutocompleteSelectedEvent): void {
    this.emitAddressData();
  }

  validateDecimal(value: string, field: 'latitude' | 'longitude'): void {
    if (!value || value.trim() === '') {
      if (field === 'latitude') {
        this.latitudError = false;
        this.latitudeErrorMessage = '';
      } else {
        this.longitudError = false;
        this.longitudeErrorMessage = '';
      }
      return;
    }

    let sanitizedValue = value.replace(',', '.').trim();
    const decimalRegex = /^-?\d+(\.\d*)?$/;
    const isValidFormat = decimalRegex.test(sanitizedValue);

    if (field === 'latitude') {
      if (!isValidFormat) {
        this.latitudError = true;
        this.latitudeErrorMessage = 'Formato inválido. Use punto (.) como separador decimal y solo números.';
      } else {
        const numValue = parseFloat(sanitizedValue);
        if (numValue < -90 || numValue > 90) {
          this.latitudError = true;
          this.latitudeErrorMessage = 'La latitud debe estar entre -90 y 90.';
        } else {
          this.latitudError = false;
          this.latitudeErrorMessage = '';
        }
      }
    } else {
      if (!isValidFormat) {
        this.longitudError = true;
        this.longitudeErrorMessage = 'Formato inválido. Use punto (.) como separador decimal y solo números.';
      } else {
        const numValue = parseFloat(sanitizedValue);
        if (numValue < -180 || numValue > 180) {
          this.longitudError = true;
          this.longitudeErrorMessage = 'La longitud debe estar entre -180 y 180.';
        } else {
          this.longitudError = false;
          this.longitudeErrorMessage = '';
        }
      }
    }
  }

  private emitAddressData(): void {
    const addressData = this.buildAddressFromForm();
    this.addressChange.emit(addressData);
  }

  private emitFormValidity(): void {
    const isValid = this.isFormValid();
    this.formValidityChange.emit(isValid);
  }

  isFormValid(): boolean {
    // Durante la inicialización, considerar el formulario como válido para evitar mostrar errores prematuros
    if (this.isInitializing) {
      return true;
    }
    return this.addressForm.valid &&
           this.locationOneCtrl.valid &&
           this.addressTypeCtrl.valid &&
           !this.latitudError &&
           !this.longitudError;
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
